import React, {useEffect, useMemo, useState} from 'react';
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogFooter,
  Button,
  CheckPanel,
  LoadingOverlay,
  Icon,
  InfoIcon,
  SelectList,
} from '@taopix/taopix-design-system';
import {useUiState} from '../Context/UserInterface/UserInterfaceContext';
import {useTaopix} from '../Context/TaopixContext';
import {useSelector} from 'react-redux';
import {LayoutSelector, TotalPageCountSelector, UserAssetSelector} from '../../Selectors';
import {useTranslation} from 'react-i18next';
import {UserInterfaceActions} from '../Context/UserInterface/UserInterfaceActionTypes';
import {
  AutoFillArgs,
  AutoFillResponse,
  AutoFlowArgs,
  AutoFlowResponse,
  ReducerState,
} from '@taopix/taopix-core/dist/types/types';
import {
  AIMode,
  AutoFlowError,
  Common,
  CurrencyAdapter,
  Lang,
  SERVER_ERROR_CODE,
} from '@taopix/taopix-core';
import {useExperience} from '../Experience/ExperienceProvider';
import {useUserFeedback} from '../Context/UserFeedback/UserFeedbackContext';
import {UserFeedbackActions} from '../Context/UserFeedback/UserFeedbackActionTypes';
import {ListItemDataType} from '@taopix/taopix-design-system/dist/types/Components/SelectList/SelectList';
import useSettings from '../../Hooks/useSettings';
import {onPageCountChanged} from '../../Utils/Page';

export type AutoFillOptionDialogProps = {
  open: boolean;
  pageType?: 'page' | 'print';
};

enum AutoFillOption {
  Standard = 'standard',
  Advanced = 'advanced',
}

const AutoFillOptionDialog = ({open, pageType = 'page', ...props}: AutoFillOptionDialogProps) => {
  const ui = useUiState();
  const app = useTaopix();
  const userFeedback = useUserFeedback();
  const settings = useSettings();
  const {t} = useTranslation(['autofill']);
  const {experience} = useExperience();

  const productLayout = useSelector(LayoutSelector);
  const pageCount = useSelector(TotalPageCountSelector);
  const [selectedAutoFillOption, setSelectedAutoFillOption] = useState<AutoFillOption>(
    AutoFillOption.Standard
  );
  const [showLoading, setShowLoading] = useState<boolean>(false);
  const [selectedPageCount, setSelectedPageCount] = useState<number>(
    ui.state.autoFillOptions.recommendedPageCount
  );

  const assets = useSelector(UserAssetSelector);
  const {isSmallScreen} = ui.state;

  useEffect(() => {
    setSelectedPageCount(ui.state.autoFillOptions.recommendedPageCount);
  }, [ui.state.autoFillOptions.recommendedPageCount]);

  const currency = useMemo(
    () => CurrencyAdapter(settings.state.currency),
    [settings.state.currency.code]
  );

  const pageList: ListItemDataType[] = useMemo(() => {
    if (!open) return [];

    const productQuantity = productLayout.pricing.quantity || 0;

    // Create a pricing engine object based on the parameters passed in.
    const pricingEngine = new (app.core.get('pricingEngine'))(
      currency,
      productQuantity,
      productLayout.pricing,
      AIMode.Off
    );

    const basePrice = pricingEngine.calculateProjectPrice(productLayout.pagecount, [], 0, 0);
    const hasPricedPaperComponent = pricingEngine.hasPricedPaperComponent(
      productLayout.minpages,
      productLayout.maxpages,
      productLayout.pageinsertcount
    );

    const labelSingular =
      pageType === 'page' ? t('optionsandextras:str_LabelPage') : t('*:str_Label1Print');
    const labelPlural =
      pageType === 'page' ? t('optionsandextras:str_LabelPages') : t('*:str_LabelNPrints');

    const pageListLength = (() => {
      if (productLayout.maxpages === productLayout.minpages) return 1;

      return Math.max(
        2,
        Math.floor(
          // Add the productLayout.pageinsertcount to make sure we have enough to include the min and max page counts.
          (productLayout.maxpages - productLayout.minpages + productLayout.pageinsertcount) /
            productLayout.pageinsertcount
        )
      );
    })();

    const pageOffset = Common.roundToNextMultiple(
      productLayout.minpages,
      productLayout.pageinsertcount
    );

    const items = Array.from(
      {
        length: pageListLength,
      },
      (_: undefined, index: number) => {
        let pageCount =
          1 === pageListLength ? pageOffset : pageOffset + index * productLayout.pageinsertcount;

        // If the value with diff goes over the max page count, reduce to the max possible page count.
        if (pageCount > productLayout.maxpages) {
          pageCount = productLayout.maxpages;
        }

        let optionLabel = (1 === pageCount ? labelSingular : labelPlural).replace(
          '^0',
          pageCount.toString()
        );

        if (
          pageCount === ui.state.autoFillOptions.recommendedPageCount &&
          ui.state.autoFillOptions.recommendedPageCount > productLayout.pagecount
        ) {
          optionLabel = `${optionLabel} (${t('*:str_MessageRecommended').toLowerCase()})`;
        }

        if (settings.state.price.showPrice && hasPricedPaperComponent) {
          // Use the pricing engine to calculate the project price.
          const newPrice = pricingEngine.calculateProjectPrice(pageCount, [], 0, 0);
          const rawPriceDiff = newPrice - basePrice;
          const priceDiff = app.core
            .get('pricingEngine')
            .formatCurrencyNumber(rawPriceDiff, currency);
          // formatCurrencyNumber will add the '-' sign for negative numbers.
          const sign: '+' | '' = rawPriceDiff >= 0 ? '+' : '';

          optionLabel = `${optionLabel} ${sign}${priceDiff}`;
        }

        return {
          label: optionLabel,
          value: pageCount.toString(),
        };
      }
    );

    /*
    Due to the fact we ignore the pageinsertcount when calculating the recommened
    there could be instances where the recommendedPageCount is not in the pageList
    as this list does respect the pageinsertcount. If it isnt add it in.
    */

    const index = items.findIndex(entry => Number(entry.value) > Number(selectedPageCount));
    if (index !== -1) {
      let optionLabel = (1 === pageCount ? labelSingular : labelPlural).replace(
        '^0',
        selectedPageCount.toString()
      );
      optionLabel = `${optionLabel} (${t('*:str_MessageRecommended').toLowerCase()})`;
      if (index === -1) {
        items.push({
          label: optionLabel,
          value: selectedPageCount.toString(),
        });
      } else {
        items.splice(index, 0, {
          label: optionLabel,
          value: selectedPageCount.toString(),
        });
      }
    }
    return items;
  }, [
    open,
    productLayout.pagecount,
    productLayout.minpages,
    productLayout.maxpages,
    ui.state.autoFillOptions.recommendedPageCount,
    assets.length,
    Lang.getCode(),
  ]);

  const onAutoFill = () => {
    setShowLoading(true);
    switch (selectedAutoFillOption) {
      case AutoFillOption.Standard:
        app.core
          .get('stateManager')
          .call<AutoFillArgs, ReducerState & AutoFillResponse>('autofill', {
            save: true,
            showLoading: false,
            sortOrder: ui.state.palette.picturePallette.sortOrder,
            sortDirection: ui.state.palette.picturePallette.sortDirection,
            includeCovers: true,
            coverItems: [],
            fromCreation: false,
            createPreviewThumbnail: true,
            checkImageUsage: true,
          })
          .then((response: any) => {
            ui.dispatch(
              UserInterfaceActions.setAutoFillOptions({
                ...ui.state.autoFillOptions,
                autoFillDialog: false,
                autoFillCompleted: true,
                autoFillNotAllPicturesUsed:
                  assets.length !== Object.keys(response.pictureUsageCount).length,
              })
            );
          })
          .catch(error => {
            setShowLoading(false);
            if (error instanceof AutoFlowError) {
              userFeedback.dispatch(
                UserFeedbackActions.setErrorUserFeedback({
                  title: '',
                  message: error.message,
                  showHeader: false,
                })
              );
            } else {
              userFeedback.dispatch(
                UserFeedbackActions.setErrorUserFeedback({
                  title: '',
                  message: app.core.get('server').getErrorFromResponse(error),
                  showHeader: SERVER_ERROR_CODE !== error.response?.status,
                })
              );
            }
            ui.dispatch(
              UserInterfaceActions.setAutoFillOptions({
                ...ui.state.autoFillOptions,
                autoFillDialog: false,
              })
            );
          })
          .finally(() => {
            setShowLoading(false);
          });
        break;
      case AutoFillOption.Advanced:
        app.core
          .get('stateManager')
          .call<AutoFlowArgs, ReducerState & AutoFlowResponse>('autoflow', {
            checkImageUsage: false,
            reassignPictures: true,
            fromCreation: false,
            pageCount: selectedPageCount,
            save: true,
            showLoading: false,
            assignPageStyleToCover1: false,
            assignPageStyleToCover2: false,
            includeTextPageStyles: true,
            createPreviewThumbnail: true,
            pageNumber: -1,
            coverItems: [],
            includeCovers: true,
            sortOrder: ui.state.palette.picturePallette.sortOrder,
            sortDirection: ui.state.palette.picturePallette.sortDirection,
          })
          .then(response => {
            ui.dispatch(
              UserInterfaceActions.setAutoFillOptions({
                ...ui.state.autoFillOptions,
                autoFillDialog: false,
                autoFillCompleted: true,
                autoFillNotAllPicturesUsed: false,
              })
            );

            ui.dispatch(
              UserInterfaceActions.setFlags({
                ...ui.state.flags,
                recommendAddingPictures: false,
              })
            );

            if (response.project.productLayout) {
              onPageCountChanged(
                {spineResized: response.data.spineresized || false},
                ui,
                response.project.productLayout,
                response.project.productLayout.pagecount
              );
            }
          })
          .catch(error => {
            if (error instanceof AutoFlowError) {
              userFeedback.dispatch(
                UserFeedbackActions.setErrorUserFeedback({
                  title: '',
                  message: error.message,
                  showHeader: false,
                })
              );
            } else {
              userFeedback.dispatch(
                UserFeedbackActions.setErrorUserFeedback({
                  title: '',
                  message: app.core.get('server').getErrorFromResponse(error),
                  showHeader: SERVER_ERROR_CODE !== error.response?.status,
                })
              );
            }
          })
          .finally(() => {
            setShowLoading(false);
          });
        break;
      default:
        break;
    }
  };

  return (
    <>
      <Dialog
        open={open}
        focusFirstElement
        trapFocus={true}
        width={isSmallScreen ? 400 : 700}
        theme={'tpx-theme-dialogs'}
        themeTip="Dialogs"
        onClose={() => {
          ui.dispatch(
            UserInterfaceActions.setAutoFillOptions({
              ...ui.state.autoFillOptions,
              autoFillDialog: false,
            })
          );
        }}
        title={t('autofill:str_TitleSelectAnAutoFillOption')}
        showTitle={false}
      >
        <DialogContent>
          <div className={'flex lg:space-x-sm sm:flex-col sm:space-y-sm'}>
            {/* Normal autoFill */}
            <CheckPanel
              title={t('autofill:str_TitleStandardAutofillOption')}
              layout={isSmallScreen ? 'horizontal' : 'vertical'}
              onClick={() => {
                setSelectedAutoFillOption(AutoFillOption.Standard);
              }}
              pressed={selectedAutoFillOption === AutoFillOption.Standard}
              content={t('autofill:str_LabelAddsPicturesIntoExistingPictureBox')}
              subtext={`${pageCount} ${t('*:str_LabelPages')}`}
              className={'flex-1'}
            />
            {/* Advanced autoflow */}
            <CheckPanel
              title={t('autofill:str_TitleAdvancedAutofillOption')}
              layout={isSmallScreen ? 'horizontal' : 'vertical'}
              onClick={() => {
                setSelectedAutoFillOption(AutoFillOption.Advanced);
              }}
              pressed={selectedAutoFillOption === AutoFillOption.Advanced}
              content={t('autofill:str_LabelCreatesFinishedDesignWithNewLayouts')}
              subtext={
                <SelectList
                  className={'mt-md'}
                  id={'pageCountSelector-'}
                  dropdownId={'pageCountDropDown-'}
                  items={pageList}
                  labelledBy={'addPagesHeading'}
                  size={isSmallScreen ? 'medium' : 'small'}
                  onChange={(event: any) => {
                    setSelectedPageCount(Number(event.target.value));
                  }}
                  selectedIndex={pageList.findIndex(p => Number(p.value) === selectedPageCount)}
                  popoutParentTheme={'tpx-theme-workarea'}
                  popoutParentThemeTip={'Work area'}
                  displayMode={isSmallScreen ? 'modal' : 'positioned'}
                  modalHeading={t('*:str_LabelPages')}
                  onKeyDown={event => {
                    setSelectedAutoFillOption(AutoFillOption.Advanced);
                  }}
                />
              }
              className={'flex-1'}
            />
          </div>
          <p className={'flex self-center items-center space-x-xs p-xs mt-sm'}>
            <Icon icon={<InfoIcon />} />
            <span>{t('autofill:str_LabelChangeMindAfterAutoFill')}</span>
          </p>
        </DialogContent>
        <DialogFooter isSmallScreen={isSmallScreen}>
          <Button
            label={t('*:str_ButtonClose')}
            buttonStyle={'tertiary'}
            onClick={() => {
              ui.dispatch(
                UserInterfaceActions.setAutoFillOptions({
                  ...ui.state.autoFillOptions,
                  autoFillDialog: false,
                })
              );
            }}
          />
          <Button
            label={t('autofill:str_LabelStartAutoFill')}
            buttonStyle={'primary'}
            onClick={onAutoFill}
          />
        </DialogFooter>
      </Dialog>
      <LoadingOverlay
        id={'autoFill-busy-overlay'}
        open={showLoading}
        label={t('*:str_MessagePleaseWait')}
        delay={500}
        key={'autoFill-busy-overlay'}
      />
    </>
  );
};

export default AutoFillOptionDialog;
